import { useMemo } from 'react';

import { Job } from '@libs/models';
import { JobCreateDto, JobUpdateDto } from '@libs/dto';
import { jobApi, emptyResponse } from '@carcareers/store';
import { useConfirmation, useLoading, useSnackbar } from '@carcareers/contexts';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { ApiError, ApiResponse } from '@libs/types';

interface JobCallbacks {
  onSuccess?: () => void;
}

export const useJob = (id?: number) => {
  const { setLoading, setLabel } = useLoading();
  const { confirm } = useConfirmation();
  const { toggleSnackbar } = useSnackbar();
  const [jobCreate] = jobApi.useJobCreateMutation();
  const [jobUpdate] = jobApi.useJobUpdateMutation();
  const [jobDelete] = jobApi.useJobDeleteMutation();
  const indexJobs = jobApi.useJobIndexQuery({ page: 1, limit: 10 });
  const viewJob = id !== undefined ? jobApi.useJobViewQuery(id) : undefined;

  return useMemo(
    () => ({
      indexJobs,
      viewJob,
      createJob: async (job?: JobCreateDto, callbacks?: JobCallbacks): Promise<ApiResponse<Job>> => {
        if (!job) {
          toggleSnackbar('warning', 'Job data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Creating job...');
        setLoading(true);

        const response = await jobCreate(job);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Job created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      updateJob: async (job?: JobUpdateDto, callbacks?: JobCallbacks): Promise<ApiResponse<Job>> => {
        if (!job) {
          toggleSnackbar('warning', 'Job data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating job...');
        setLoading(true);

        const response = await jobUpdate(job);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Job updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      deleteJob: async (id?: number, callbacks?: JobCallbacks) => {
        if (!id) return;

        const confirmed = await confirm({
          title: 'Close Job and Delete Template',
          message: 'Are you sure you want to close this job and delete the template? This action is irreversible and will close any active jobs and delete the template.',
        });

        if (!confirmed) return;

        setLabel('Deleting job...');
        setLoading(true);

        const response = await jobDelete(id);
        setLoading(false);

        if (!isError(response)) {
          toggleSnackbar('success', `Deleted job ${id}`);
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          toggleSnackbar('error', getErrorsAsString(response));
        }

        return response;
      },
      findByRooftop: (rooftopId?: number) =>
        rooftopId ? jobApi.useJobFindByRooftopQuery({ rooftopId, page: 1, limit: 10 }) : emptyResponse,
    }),
    [
      jobCreate,
      jobUpdate,
      jobDelete,
      setLoading,
      setLabel,
      indexJobs,
      viewJob,
      confirm,
      toggleSnackbar,
    ],
  );
};
